/* eslint-disable react/no-unescaped-entities */
import * as React from "react";
import {
    Box,
    TextField,
    Autocomplete,
    Grid,
    Typography,
    IconButton,
    InputAdornment,
    Dialog,
    DialogContentText,
    DialogActions,
    Button,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import RoomOutlinedIcon from "@mui/icons-material/RoomOutlined";
import PublicIcon from "@mui/icons-material/Public";
import ClearIcon from "@mui/icons-material/Clear";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import parse from "autosuggest-highlight/parse";
import { debounce } from "@mui/material/utils";

const COUNTRY = "ae";
const autocompleteService = { current: null };
const apiKey = process.env.MAPKEY;

function loadGoogleMaps(apiKey) {
    if (typeof window === "undefined") return;
    if (document.getElementById("google-maps")) return;

    const script = document.createElement("script");
    script.id = "google-maps";
    script.async = true;
    script.defer = true;
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    document.head.appendChild(script);
}

export default function PickupLocation({
    type,
    label,
    placeholder,
    value,
    onChange,
    newDesign,
    error,
    helperText,
    size = "medium",
    disabled = false,
}) {
    const GOOGLE_MAPS_API_KEY = process.env.MAPKEY;;
    const [inputValue, setInputValue] = React.useState(value?.address || "");
    const [options, setOptions] = React.useState([]);
    const [alertShown, setAlertShown] = React.useState(false);
    const [selected, setSelected] = React.useState(value || null);
    const sessionTokenRef = React.useRef(null);


    // ✅ keep local input synced with external value
    React.useEffect(() => {
        if (value?.address) {
            setInputValue(value.address);
            setSelected(value);
        } else {
            setInputValue("");
            setSelected(null);
        }
    }, [value]);

    // ✅ Load Google Maps once
    React.useEffect(() => {
        loadGoogleMaps(GOOGLE_MAPS_API_KEY);
    }, [GOOGLE_MAPS_API_KEY]);

    // ✅ Initialize AutocompleteService + token
    React.useEffect(() => {
        const interval = setInterval(() => {
            if (window.google?.maps?.places) {
                clearInterval(interval);
                if (!autocompleteService.current) {
                    autocompleteService.current =
                        new window.google.maps.places.AutocompleteService();
                }
                if (!sessionTokenRef.current) {
                    sessionTokenRef.current =
                        new window.google.maps.places.AutocompleteSessionToken();
                }
            }
        }, 500);
        return () => clearInterval(interval);
    }, []);

    // ✅ Debounced prediction fetching
    const fetchPredictions = React.useMemo(
        () =>
            debounce((request, callback) => {
                if (!autocompleteService.current) return;
                const req = {
                    ...request,
                    componentRestrictions: { country: COUNTRY },
                    sessionToken: sessionTokenRef.current,
                };
                autocompleteService.current.getPlacePredictions(req, callback);
            }, 400),
        []
    );

    // ✅ Fetch predictions while typing
    React.useEffect(() => {
        let active = true;
        if (!autocompleteService.current || inputValue.trim() === "") {
            setOptions(selected ? [selected] : []);
            return;
        }

        fetchPredictions({ input: inputValue }, (results) => {
            if (!active) return;
            let newOptions = selected ? [selected] : [];
            if (results?.length) newOptions = [...newOptions, ...results];
            setOptions(newOptions);
        });

        return () => {
            active = false;
        };
    }, [inputValue, selected, fetchPredictions]);

    const getPlaceDetails = (option) => {
        if (!option?.place_id || !window.google?.maps?.places) return;

        const service = new window.google.maps.places.PlacesService(
            document.createElement("div")
        );

        service.getDetails(
            {
                placeId: option.place_id,
                fields: ["formatted_address", "geometry", "name", "place_id"],
            },
            (details, status) => {
                if (status === window.google.maps.places.PlacesServiceStatus.OK) {
                    const locationData = {
                        // 🧠 Use `option.description` as the main address (more accurate visually)
                        address: option.description || details.formatted_address,
                        lat: details.geometry?.location?.lat(),
                        lng: details.geometry?.location?.lng(),
                        place_id: details.place_id,
                        name:
                            option.structured_formatting?.main_text ||
                            details.name ||
                            option.description,
                        // optionally keep the raw formatted address too
                        formatted_address: details.formatted_address,
                    };

                    setSelected(option);
                    setInputValue(locationData.address);
                    onChange?.(locationData);

                    // renew session token
                    sessionTokenRef.current =
                        new window.google.maps.places.AutocompleteSessionToken();
                }
            }
        );
    };



    // ✅ Handle dialog close
    const handleClose = () => setAlertShown(false);

    // ✅ Input border style
    const inputBorderSx = newDesign
        ? {
            "& .MuiOutlinedInput-root": {
                "& fieldset": { border: "none" },
                "&:hover fieldset": { border: "none" },
                "&.Mui-focused fieldset": { border: "none" },
            },
        }
        : {
            "& .MuiOutlinedInput-root": {
                "& fieldset": {
                    border: "1px solid rgba(0, 0, 0, 0.5)",
                    borderRadius: "8px",
                },
                "&:hover fieldset": { border: "1px solid rgba(0, 0, 0, 0.5)" },
                "&.Mui-focused fieldset": { border: "1px solid rgba(0, 0, 0, 0.5)" },
            },
        };

    return (
        <Box sx={{ width: "100%", ...inputBorderSx }}>
            <Autocomplete
                fullWidth
                id={`${type}-location`}
                size={size}
                options={options}
                disabled={disabled}
                value={selected}
                filterOptions={(x) => x} // don’t filter client-side
                getOptionLabel={(option) =>
                    typeof option === "string"
                        ? option
                        : option.description || option.address || ""
                }
                includeInputInList
                autoComplete
                popupIcon={null} // remove the default dropdown icon
                disableClearable // we’ll handle our own clear button
                onChange={(_, newValue) => {
                    if (newValue) getPlaceDetails(newValue);
                }}
                inputValue={inputValue}
                onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
                noOptionsText="" // hide “No locations found”
                openOnFocus={false} // don’t show empty dropdown
                renderInput={(params) => (
                    <TextField
                        {...params}
                        placeholder={placeholder}
                        variant="outlined"
                        sx={{
                            "& .MuiOutlinedInput-root": {
                                borderRadius: "999px",
                                border: "1px solid #d0d0d0",
                                backgroundColor: "#fff",
                                "& input": {
                                    fontSize: "14px",
                                    padding: "10px 14px",
                                    color: "#222",
                                    textOverflow: "ellipsis",
                                },
                                "& fieldset": { border: "none" },
                                "&:hover": {
                                    backgroundColor: "#f9f9f9",
                                    borderColor: "#c0c0c0",
                                },
                                "&.Mui-focused": {
                                    backgroundColor: "#fff",
                                    boxShadow: "0 0 0 2px rgba(25,118,210,0.2)",
                                    borderColor: "#1976d2",
                                },
                            },
                            "& .MuiAutocomplete-endAdornment": { right: "8px" },
                        }}
                        InputProps={{
                            ...params.InputProps,
                            // startAdornment: (
                            //     <InputAdornment position="start">
                            //         <RoomOutlinedIcon sx={{ color: "#888", fontSize: 20, ml: 1 }} />
                            //     </InputAdornment>
                            // ),
                            endAdornment: selected ? (
                                <Button
                                    onClick={() => {
                                        setSelected(null);
                                        setInputValue("");
                                        onChange?.({
                                            address: "",
                                            lat: "",
                                            lng: "",
                                            place_id: "",
                                            name: "",
                                        });
                                    }}
                                    edge="end"
                                    sx={{ color: "#888", mr: 0.5 }}
                                >
                                    <ClearIcon sx={{ fontSize: 18 }} />
                                </Button>
                            ) : null,
                        }}
                        fullWidth
                    />
                )}
                renderOption={(props, option, { selected }) => (
                    <li
                        {...props}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "10px",
                            padding: "10px 14px",
                            cursor: "pointer",
                            backgroundColor: selected ? "#53b5ad" : "#fff",
                            color: selected ? "#fff" : "#000",
                        }}
                    >
                        <RoomOutlinedIcon
                            sx={{
                                fontSize: 22,
                                color: selected ? "#004d46" : "#666",
                                flexShrink: 0,
                            }}
                        />
                        <Box>
                            <Typography
                                variant="body2"
                                fontWeight={600}
                                sx={{
                                    lineHeight: 1.2,
                                    color: selected ? "#fff" : "#000",
                                }}
                            >
                                {option.structured_formatting?.main_text || ""}
                            </Typography>
                            <Typography
                                variant="body2"
                                sx={{
                                    fontSize: "13px",
                                    lineHeight: 1.1,
                                    color: selected ? "rgba(255,255,255,0.85)" : "rgba(0,0,0,0.6)",
                                }}
                            >
                                {option.structured_formatting?.secondary_text || ""}
                            </Typography>
                        </Box>
                    </li>
                )}


                PaperComponent={({ children }) =>
                    !options.length ? null : (
                        <Box
                            sx={{
                                borderRadius: "8px",
                                overflow: "hidden",
                                boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
                                backgroundColor: "#fff",
                                "& .MuiAutocomplete-option:hover": {
                                    backgroundColor: "#53b5ad !important",
                                    "& svg": { color: "#004d46 !important" },
                                    "& *": { color: "#fff !important" },
                                },
                            }}
                        >
                            {children}
                        </Box>
                    )
                }


            />


            {/* 🔒 Location permission alert */}
            <Dialog open={alertShown} onClose={handleClose}>
                <Box textAlign="center" p={2}>
                    <Box
                        sx={{
                            backgroundColor: "#ffecec",
                            color: "#e53935",
                            borderRadius: "50%",
                            p: 1.5,
                            mb: 2,
                            display: "inline-flex",
                        }}
                    >
                        <LocationOffIcon />
                    </Box>
                    <Typography variant="h6" fontWeight={600}>
                        Location Access Disabled
                    </Typography>
                    <DialogContentText sx={{ mt: 1, color: "#555" }}>
                        Please enable location permission in your browser settings.
                    </DialogContentText>
                    <DialogActions sx={{ justifyContent: "center" }}>
                        <Button
                            variant="outlined"
                            onClick={handleClose}
                            sx={{
                                color: "#6dc8bf",
                                borderColor: "#6dc8bf",
                                textTransform: "capitalize",
                                borderRadius: "8px",
                            }}
                        >
                            Close
                        </Button>
                    </DialogActions>
                </Box>
            </Dialog>
        </Box>
    );
}
